import { useState } from "react";
import { ApiError, api } from "../lib/api.ts";
import { RISK_LABEL, STATUS_LABEL } from "../lib/changes.ts";
import { relativeDate } from "../lib/format.ts";
import { navigate, onLink } from "../lib/router.ts";
import { useGet, useSession } from "../lib/session.tsx";
import type { ChangesetView, PageChange, RoleChange, RoleRule, WorkflowChange } from "../lib/types.ts";
import { actionText, triggerText } from "../lib/workflows.ts";

const VIA = (v: string) => (v === "mcp" ? "pela IA" : v === "browser" ? "no app" : v === "api" ? "pela API" : v);

export function ChangesetList() {
  const s = useSession();
  const [status, setStatus] = useState<"pending" | "">("pending");
  const { data, error, reload } = useGet<{ changesets: ChangesetView[] }>(`/v1/changesets?limit=100${status ? `&status=${status}` : ""}`);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const list = data?.changesets ?? [];
  const batchable = list.filter((c) => c.status === "pending" && c.risk !== "destrutivo");

  async function approveSelected() {
    setBusy(true);
    setMsg(null);
    let ok = 0;
    const failed: string[] = [];
    // Oldest first: later changesets may build on earlier ones.
    for (const cs of [...list].reverse().filter((c) => picked.has(c.id))) {
      try {
        await api("POST", `/v1/changesets/${cs.id}/apply`, { hash: cs.hash });
        ok++;
      } catch (e) {
        failed.push(`${cs.title}: ${e instanceof Error ? e.message : "erro"}`);
      }
    }
    setPicked(new Set());
    setBusy(false);
    setMsg(failed.length ? `${ok} aplicados. Falharam: ${failed.join("; ")}` : `${ok} aplicados.`);
    reload();
    await s.reloadStructure();
  }

  return (
    <div className="stack">
      <div className="topbar">
        <h1>Mudanças</h1>
        <span className="seg">
          <button aria-pressed={status === "pending"} onClick={() => setStatus("pending")}>
            Pendentes
          </button>
          <button aria-pressed={status === ""} onClick={() => setStatus("")}>
            Histórico
          </button>
        </span>
        <span className="spacer" />
        {s.canBuild && status === "pending" && (
          <button className="btn primary" disabled={!picked.size || busy} onClick={approveSelected}>
            Aprovar selecionados{picked.size ? ` (${picked.size})` : ""}
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Toda mudança de estrutura (coleções, campos e telas) vira um changeset. Os da IA esperam sua aprovação aqui; os destrutivos se aprovam um a um, na revisão.
      </p>
      {msg && <p className="ok">{msg}</p>}
      {error && <p className="error">{error.message}</p>}
      <div className="card list">
        {status === "pending" && s.canBuild && batchable.length > 1 && (
          <label className="checkline muted">
            <input type="checkbox" checked={picked.size === batchable.length} onChange={(e) => setPicked(new Set(e.target.checked ? batchable.map((c) => c.id) : []))} />
            Selecionar todos
          </label>
        )}
        {list.map((cs) => (
          <div key={cs.id} className="list-item">
            {status === "pending" && s.canBuild && (
              <input
                type="checkbox"
                aria-label={`Selecionar ${cs.title}`}
                disabled={cs.risk === "destrutivo"}
                checked={picked.has(cs.id)}
                onChange={(e) =>
                  setPicked((p) => {
                    const n = new Set(p);
                    if (e.target.checked) n.add(cs.id);
                    else n.delete(cs.id);
                    return n;
                  })
                }
              />
            )}
            <a href={`/changesets/${cs.id}`} onClick={onLink} style={{ fontWeight: 600 }}>
              {cs.title}
            </a>
            <span className={`risk ${cs.risk}`}>{RISK_LABEL[cs.risk]}</span>
            {status === "" && <span className="chip gray">{STATUS_LABEL[cs.status]}</span>}
            <span className="spacer" />
            <span className="muted" style={{ fontSize: 12.5 }}>
              {VIA(cs.createdVia)} · {relativeDate(cs.createdAt)}
            </span>
          </div>
        ))}
        {data && !list.length && <p className="muted" style={{ margin: 0 }}>{status ? "Nada esperando aprovação." : "Nenhuma mudança ainda."}</p>}
      </div>
    </div>
  );
}

const show = (v: unknown) => (typeof v === "string" ? v : JSON.stringify(v));

function PropsDiff({ before, after }: { before?: Record<string, unknown>; after?: Record<string, unknown> }) {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter(
    (k) => JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]),
  );
  if (!before || !after || !keys.length) return null;
  return (
    <ul>
      {keys.map((k) => (
        <li key={k}>
          <code>{k}</code>: {before[k] === undefined ? "—" : show(before[k])} → {after[k] === undefined ? "—" : show(after[k])}
        </li>
      ))}
    </ul>
  );
}

const CHANGE = { created: "nova", removed: "removida", changed: "alterada", moved: "movida" } as const;

function PageDiff({ p }: { p: PageChange }) {
  return (
    <div className={`diff-item ${p.change}`}>
      <strong>Tela {p.after?.title ?? p.before?.title ?? p.page}</strong> <span className="muted">({CHANGE[p.change]})</span>
      <PropsDiff before={p.before as never} after={p.after as never} />
      {p.blocks.length > 0 && (
        <ul>
          {p.blocks.map((b) => (
            <li key={b.block}>
              Bloco <code>{b.block}</code> ({b.type}): {{ created: "adicionado", removed: "removido", changed: "alterado", moved: "movido" }[b.change]}
              {b.change === "changed" && <PropsDiff before={b.before} after={b.after} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SCOPE_TEXT = { all: "todos", own: "só os próprios", none: "nenhum" } as const;

/** One rule in words: "pedidos: vê só os próprios, cria, edita só os próprios; oculto: custo". */
export function ruleText(r: RoleRule): string {
  const parts = [`vê ${SCOPE_TEXT[r.read]}`];
  if (r.create) parts.push("cria");
  if (r.update !== "none") parts.push(`edita ${SCOPE_TEXT[r.update]}`);
  if (r.delete !== "none") parts.push(`apaga ${SCOPE_TEXT[r.delete]}`);
  if (r.filter) parts.push(`filtro ${JSON.stringify(r.filter)}`);
  if (r.hidden?.length) parts.push(`oculto: ${r.hidden.join(", ")}`);
  if (r.readOnly?.length) parts.push(`só leitura: ${r.readOnly.join(", ")}`);
  return `${r.collection}: ${parts.join(", ")}`;
}

function RoleDiff({ r }: { r: RoleChange }) {
  const before = new Map((r.before?.rules ?? []).map((x) => [x.collection, ruleText(x)]));
  const after = new Map((r.after?.rules ?? []).map((x) => [x.collection, ruleText(x)]));
  const rules = [...new Set([...before.keys(), ...after.keys()])].filter((k) => before.get(k) !== after.get(k));
  const { rules: _b, ...pb } = r.before ?? { rules: [] };
  const { rules: _a, ...pa } = r.after ?? { rules: [] };
  return (
    <div className={`diff-item ${r.change}`}>
      <strong>Papel {r.after?.label ?? r.before?.label ?? r.role}</strong> <span className="muted">({{ created: "novo", removed: "removido", changed: "alterado" }[r.change]})</span>
      <PropsDiff before={r.before ? pb : undefined} after={r.after ? pa : undefined} />
      {rules.length > 0 && (
        <ul>
          {rules.map((k) => (
            <li key={k}>
              {before.has(k) && after.has(k) ? (
                <>
                  <s className="muted">{before.get(k)}</s> → {after.get(k)}
                </>
              ) : after.has(k) ? (
                after.get(k)
              ) : (
                <s className="muted">{before.get(k)}</s>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WorkflowDiff({ w }: { w: WorkflowChange }) {
  const lines = (p: WorkflowChange["after"]) => (p ? [triggerText(p.trigger), ...p.actions.map((a, i) => `${i + 1}. ${actionText(a)}`)] : []);
  const before = lines(w.before);
  const after = lines(w.after);
  const pb = w.before && { label: w.before.label, description: w.before.description, enabled: w.before.enabled };
  const pa = w.after && { label: w.after.label, description: w.after.description, enabled: w.after.enabled };
  const same = before.join("\n") === after.join("\n");
  return (
    <div className={`diff-item ${w.change}`}>
      <strong>Workflow {w.after?.label ?? w.before?.label ?? w.workflow}</strong> <span className="muted">({{ created: "novo", removed: "removido", changed: "alterado" }[w.change]})</span>
      {w.change === "changed" && <PropsDiff before={pb as never} after={pa as never} />}
      {!same && (
        <ul>
          {w.change === "changed" && before.map((l, i) => <li key={`b${i}`}><s className="muted">{l}</s></li>)}
          {(w.change === "removed" ? before : after).map((l, i) => (
            <li key={`a${i}`}>{w.change === "removed" ? <s className="muted">{l}</s> : l}</li>
          ))}
        </ul>
      )}
      {w.after && !w.after.enabled && w.change === "created" && <p className="muted" style={{ margin: 0 }}>Criado desligado.</p>}
    </div>
  );
}

export function ChangesetReview({ id }: { id: string }) {
  const s = useSession();
  const { data, error, reload } = useGet<{ changeset: ChangesetView }>(`/v1/changesets/${id}`);
  const rollback = useGet<{ preview: { risk: ChangesetView["risk"]; impact: ChangesetView["impact"] } }>(
    data?.changeset.status === "applied" ? `/v1/changesets/${id}/rollback` : null,
  );
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (error) return <p className="error">{error.message}</p>;
  if (!data) return <p className="muted">Carregando…</p>;
  const cs = data.changeset;

  async function act(path: string, body?: unknown, done?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api("POST", `/v1/changesets/${id}/${path}`, body);
      await s.reloadStructure();
      if (done) s.toast(done);
      setConfirmed(false);
      reload();
      rollback.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? [e.message, ...e.details.map((d) => d.message)].join(" ") : "Erro");
    } finally {
      setBusy(false);
    }
  }

  const destructive = cs.risk === "destrutivo";
  const rbRisk = rollback.data?.preview.risk;

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <div className="topbar">
        <a href="/changesets" onClick={onLink} className="muted">
          ← Mudanças
        </a>
      </div>
      <div className="topbar">
        <h1>{cs.title}</h1>
        <span className={`risk ${cs.risk}`}>{RISK_LABEL[cs.risk]}</span>
        <span className="chip gray">{STATUS_LABEL[cs.status]}</span>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Proposto {VIA(cs.createdVia)} em {relativeDate(cs.createdAt)}
        {cs.decidedAt ? ` · decidido em ${relativeDate(cs.decidedAt)}` : ""}
        {cs.rolledBackAt ? ` · desfeito em ${relativeDate(cs.rolledBackAt)}` : ""}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>O que muda</h3>
        {cs.diff.map((c) => (
          <div key={c.collection} className={`diff-item ${c.change}`}>
            <strong>Coleção {c.after?.label ?? c.before?.label ?? c.collection}</strong> <span className="muted">({CHANGE[c.change]})</span>
            <PropsDiff before={c.before as never} after={c.after as never} />
            {c.fields.length > 0 && (
              <ul>
                {c.fields.map((f) => (
                  <li key={f.field}>
                    Campo <code>{f.field}</code>: {{ created: "novo", removed: "removido", changed: "alterado" }[f.change]}
                    {f.change === "changed" && <PropsDiff before={f.before} after={f.after} />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {cs.pages.map((p) => (
          <PageDiff key={p.page} p={p} />
        ))}
        {(cs.roles ?? []).map((r) => (
          <RoleDiff key={r.role} r={r} />
        ))}
        {(cs.workflows ?? []).map((w) => (
          <WorkflowDiff key={w.workflow} w={w} />
        ))}
        {!cs.diff.length && !cs.pages.length && !cs.roles?.length && !cs.workflows?.length && <p className="muted">Nenhuma diferença em relação à estrutura atual.</p>}
      </div>

      {cs.impact.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Impacto nos dados</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {cs.impact.map((i, n) => (
              <li key={n} className={`impact ${i.severity}`}>
                {i.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <details className="card">
        <summary className="muted">Operações ({cs.ops.length})</summary>
        <pre className="code" style={{ marginTop: 10 }}>{JSON.stringify(cs.ops, null, 2)}</pre>
      </details>

      {err && <p className="error">{err}</p>}
      {s.canBuild && cs.status === "pending" && (
        <div className="row">
          {destructive && (
            <label className="checkline">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              Entendo que esta mudança é destrutiva
            </label>
          )}
          <button className="btn primary" disabled={busy || (destructive && !confirmed)} onClick={() => act("apply", { hash: cs.hash, confirmDestructive: confirmed }, "Mudança aplicada.")}>
            Aprovar e aplicar
          </button>
          <button className="btn danger" disabled={busy} onClick={() => act("reject", undefined, "Mudança rejeitada.")}>
            Rejeitar
          </button>
        </div>
      )}
      {s.canBuild && cs.status === "applied" && (
        <div className="row">
          {rbRisk === "destrutivo" && (
            <label className="checkline">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              Desfazer é destrutivo; confirmo
            </label>
          )}
          <button
            className="btn"
            disabled={busy || !rbRisk || (rbRisk === "destrutivo" && !confirmed)}
            onClick={() => confirm("Desfazer esta mudança?") && act("rollback", { confirmDestructive: confirmed }, "Mudança desfeita.")}
          >
            Desfazer
          </button>
          {rollback.error && <span className="muted">{rollback.error.message}</span>}
        </div>
      )}
      {cs.status !== "pending" && (
        <div>
          <button className="linkish" onClick={() => navigate("/changesets")}>
            Voltar à lista
          </button>
        </div>
      )}
    </div>
  );
}
